import { useMemo, useState } from 'react';
import type { Organization } from '../lib/api';
import type { MyTaskItem } from '../lib/lmsApi';
import { Icon } from '../ui/icons';
import { formatDateShort, formatDayLong, formatRelative } from './dates';

/** Overdue, due soon, or comfortably ahead — decides the card's accent. */
function urgency(item: MyTaskItem): 'done' | 'overdue' | 'soon' | 'later' {
  if (item.done) return 'done';
  if (!item.task.dueAt) return 'later';
  const due = new Date(item.task.dueAt).getTime();
  const now = Date.now();
  if (due < now) return 'overdue';
  return due - now < 48 * 60 * 60 * 1000 ? 'soon' : 'later';
}

const URGENCY_LABEL = {
  done: 'Сдано',
  overdue: 'Просрочено',
  soon: 'Скоро срок',
  later: 'В работе',
} as const;

export interface MyTasksViewProps {
  org: Organization;
  items: MyTaskItem[];
  loading: boolean;
  onOpenTask: (taskId: string) => void;
  onToggleDone: (item: MyTaskItem, done: boolean) => void | Promise<void>;
}

type Filter = 'open' | 'all';

/**
 * The student's homework as a full page, not a side panel.
 *
 * Subject → topic → deadline is the scan order: a student opens this to answer
 * "what do I still have to do", so pending work leads and each card is large
 * enough to open without hunting for a tiny row.
 */
export function MyTasksView({ org, items, loading, onOpenTask, onToggleDone }: MyTasksViewProps) {
  const [filter, setFilter] = useState<Filter>('open');

  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      if (a.done !== b.done) return a.done ? 1 : -1;
      const ad = a.task.dueAt ? new Date(a.task.dueAt).getTime() : Number.MAX_SAFE_INTEGER;
      const bd = b.task.dueAt ? new Date(b.task.dueAt).getTime() : Number.MAX_SAFE_INTEGER;
      return ad - bd;
    });
  }, [items]);

  const visible = filter === 'all' ? sorted : sorted.filter((item) => !item.done);
  const pending = items.filter((item) => !item.done).length;
  const overdue = items.filter((item) => urgency(item) === 'overdue').length;
  const doneCount = items.length - pending;
  const next = sorted.find((item) => !item.done) ?? null;

  return (
    <section className="hw-page">
      <header className="hw-hero">
        <div className="hw-hero-copy">
          <p className="hw-kicker">
            <Icon name="clipboard" size={14} />
            {org.name}
          </p>
          <h1>Домашняя работа</h1>
          <p className="hw-hero-lead">
            {loading
              ? 'Загрузка заданий…'
              : items.length === 0
                ? 'Когда учитель задаст работу, она появится здесь и в календаре.'
                : pending === 0
                  ? 'Все текущие задания сданы — можно выдохнуть.'
                  : next?.task.dueAt
                    ? `Ближайшее — «${next.task.title}», срок ${formatRelative(next.task.dueAt)}.`
                    : `Осталось сдать ${pending} из ${items.length}.`}
          </p>
        </div>

        {!loading && items.length > 0 && (
          <div className="hw-stats" aria-label="Сводка по заданиям">
            <div className="hw-stat">
              <strong>{pending}</strong>
              <span>осталось</span>
            </div>
            <div className={`hw-stat ${overdue > 0 ? 'is-warn' : ''}`}>
              <strong>{overdue}</strong>
              <span>просрочено</span>
            </div>
            <div className="hw-stat is-ok">
              <strong>{doneCount}</strong>
              <span>сдано</span>
            </div>
          </div>
        )}
      </header>

      {items.length > 0 && (
        <div className="hw-toolbar">
          <div className="seg-group" role="tablist" aria-label="Фильтр заданий">
            <button
              type="button"
              role="tab"
              aria-selected={filter === 'open'}
              className={filter === 'open' ? 'is-active' : ''}
              onClick={() => setFilter('open')}
            >
              Активные
              <em>{pending}</em>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={filter === 'all'}
              className={filter === 'all' ? 'is-active' : ''}
              onClick={() => setFilter('all')}
            >
              Все
              <em>{items.length}</em>
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="hw-grid" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="hw-card-skel" />
          ))}
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="hw-empty">
          <span className="hw-empty-icon" aria-hidden="true">
            <Icon name="clipboard" size={32} />
          </span>
          <h2>Заданий пока нет</h2>
          <p>Как только учитель задаст домашнюю работу, она появится на этой странице и в календаре.</p>
        </div>
      )}

      {!loading && items.length > 0 && visible.length === 0 && (
        <div className="hw-empty">
          <span className="hw-empty-icon is-ok" aria-hidden="true">
            <Icon name="check" size={32} />
          </span>
          <h2>Всё сдано</h2>
          <p>Активных заданий не осталось. Откройте «Все», чтобы посмотреть историю.</p>
          <button type="button" className="ghost-btn" onClick={() => setFilter('all')}>
            Показать все
          </button>
        </div>
      )}

      {!loading && visible.length > 0 && (
        <ul className="hw-grid">
          {visible.map((item, index) => {
            const tone = urgency(item);
            // Ticked by the student, not yet accepted by the teacher. Older
            // servers send no `accepted`; there a tick simply reads as done.
            const waiting = item.done && item.accepted === false;
            const photos = item.task.attachments.filter((f) => f.mime.startsWith('image/'));
            const files = item.task.attachments.length - photos.length;

            return (
              <li
                key={item.assignmentId}
                className={`hw-tile is-${tone}`}
                style={{ '--hw-i': index } as React.CSSProperties}
              >
                <div className="hw-tile-top">
                  <span className={`hw-tone is-${waiting ? 'soon' : tone}`}>
                    {waiting ? 'На проверке' : URGENCY_LABEL[tone]}
                  </span>
                  <label
                    className="hw-tile-check"
                    title={
                      item.accepted
                        ? 'Учитель принял работу'
                        : item.done
                          ? 'Сдано, ждёт проверки'
                          : 'Отметить сдано'
                    }
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={item.done}
                      // Accepted work is the teacher's call to reopen.
                      disabled={item.accepted === true}
                      onChange={(e) => void onToggleDone(item, e.target.checked)}
                    />
                    <span>{item.accepted ? 'Принято' : item.done ? 'Сдано' : 'Сдать'}</span>
                  </label>
                </div>

                <button type="button" className="hw-tile-body" onClick={() => onOpenTask(item.task.id)}>
                  {item.task.subject && <span className="subject-badge">{item.task.subject}</span>}
                  <h2 className={item.done ? 'is-done' : ''}>{item.task.title}</h2>

                  {item.task.description && (
                    <p className="hw-tile-desc">{item.task.description}</p>
                  )}

                  <div className="hw-tile-meta">
                    <span className={tone === 'overdue' ? 'is-overdue' : ''}>
                      <Icon name="clock" size={14} />
                      {item.task.dueAt
                        ? `${formatDateShort(item.task.dueAt)} · ${formatRelative(item.task.dueAt)}`
                        : 'без срока'}
                    </span>
                    {item.teacher && (
                      <span>
                        <Icon name="graduation" size={14} />
                        {item.teacher.name}
                      </span>
                    )}
                    {item.task.attachments.length > 0 && (
                      <span>
                        <Icon name="paperclip" size={14} />
                        {photos.length > 0 && `${photos.length} фото`}
                        {photos.length > 0 && files > 0 && ' · '}
                        {files > 0 && `${files} файл(ов)`}
                        {photos.length === 0 && files === 0 && item.task.attachments.length}
                      </span>
                    )}
                  </div>

                  {item.boardId && (
                    <span className="hw-board-chip">
                      <Icon name="boards" size={13} />
                      {item.boardName ?? 'Доска решения'}
                    </span>
                  )}
                  {!item.boardId && (item.submissionAttachments?.length ?? 0) > 0 && (
                    <span className="hw-board-chip is-files">
                      <Icon name="paperclip" size={13} />
                      Решение: {item.submissionAttachments.length} файл(ов)
                    </span>
                  )}

                  {item.task.dueAt && (
                    <p className="hw-tile-due-long">{formatDayLong(new Date(item.task.dueAt))}</p>
                  )}

                  {photos.length > 0 && (
                    <div className="hw-tile-photos" aria-hidden="true">
                      {photos.slice(0, 3).map((photo) => (
                        <img key={photo.id} src={photo.url} alt="" loading="lazy" />
                      ))}
                      {photos.length > 3 && <span>+{photos.length - 3}</span>}
                    </div>
                  )}

                  <span className="hw-tile-open">
                    Открыть задание
                    <Icon name="chevronDown" size={14} className="rotate-270" />
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
